/**
 * Aspect — Revive Mode overlay (DOM side)
 * --------------------------------------------------------------
 * Pure DOM manager for the interactive 2D render state:
 *   - full-screen layer covering the WebGL canvas with the (AI) render image,
 *   - invisible hitboxes rebuilt from ReviveMode's normalized rects,
 *   - hover pricing card (list price struck out, −% badge, sale price),
 *   - a smaller glass-circle cursor that tracks the pointer over this layer,
 *   - "Enter Tour" button that asks the engine to restore, then cross-fades
 *     this layer away over the freshly-rendered live canvas.
 *
 * No PlayCanvas imports here — the engine side (interaction/revive_mode.js)
 * calls show()/fadeOutAndRemove() and nothing else.
 */

import { ReviveOutlineEditor, polyToRing, rectToPoly, ringToD } from "./revive_outline_edit.js";

export class ReviveOverlay {
  /**
   * @param {{ onEnterTour: () => void }} callbacks
   */
  constructor(callbacks = {}) {
    this.onEnterTour = callbacks.onEnterTour ?? (() => {});
    this.onOpenGallery = callbacks.onOpenGallery ?? (() => {});
    this.onPhotoreal = callbacks.onPhotoreal ?? (() => {});
    this.onHover = callbacks.onHover ?? (() => {});
    this.onHitboxesChange = callbacks.onHitboxesChange ?? (() => {});
    /** @type {HTMLElement|null} */
    this.root = null;
    this._cursor = null;
    this._escHandler = null;
    this._ptrMove = null;
    this._hotHb = null;
    this._image = null;
    this._photorealBtn = null;
    this._tweakBtn = null;
    this._editor = null;
    this._status = null;
    this._wait = null;
  }

  /**
   * Build and show the 2D layer.
   * @param {object} state    snapshot from ReviveMode.captureReviveState()
   * @param {{ image: string|null, photoreal?: boolean }} opts
   */
  show(state, { image, photoreal = false } = {}) {
    this.remove(); // never stack two layers

    const root = document.createElement("div");
    root.className = "revive-layer" + (photoreal ? " revive-layer--photoreal" : "");
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-label", "Interactive AI render");

    // --- the flat render ---------------------------------------------------
    if (image) {
      const img = document.createElement("img");
      img.className = "revive-layer__img";
      img.src = image;
      img.alt = "AI-generated render of the current view";
      img.draggable = false;
      root.appendChild(img);
    } else {
      root.classList.add("revive-layer--noimg");
    }

    // --- badge + enter button ----------------------------------------------
    const badge = document.createElement("div");
    badge.className = "revive-layer__badge";
    badge.textContent = "AI Render — hover for prices · Tweak outlines to fit";
    root.appendChild(badge);

    const enter = document.createElement("button");
    enter.type = "button";
    enter.className = "revive-layer__enter";
    enter.innerHTML =
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg> Enter Tour';
    enter.addEventListener("click", async () => {
      if (enter.disabled) return;
      enter.disabled = true;
      try {
        await this.onEnterTour();
      } finally {
        if (this.root && enter.isConnected) enter.disabled = false;
      }
    });
    root.appendChild(enter);

    const photorealBtn = document.createElement("button");
    photorealBtn.type = "button";
    photorealBtn.className = "revive-layer__photoreal";
    photorealBtn.textContent = "Photoreal";
    photorealBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.onPhotoreal();
    });
    this._photorealBtn = photorealBtn;

    const tweakBtn = document.createElement("button");
    tweakBtn.type = "button";
    tweakBtn.className = "revive-layer__tweak is-on";
    tweakBtn.textContent = "Tweak";
    tweakBtn.setAttribute("aria-pressed", "true");
    tweakBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this._setTweak(!this._editor?.enabled);
    });
    this._tweakBtn = tweakBtn;

    const galleryBtn = document.createElement("button");
    galleryBtn.type = "button";
    galleryBtn.className = "revive-layer__gallery";
    galleryBtn.textContent = "Gallery";
    galleryBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.onOpenGallery();
    });
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "revive-layer__remove";
    removeBtn.textContent = "Remove";
    removeBtn.disabled = true;
    removeBtn.title = "Remove the selected outline";
    removeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this._editor?.removeSelected();
    });
    this._removeBtn = removeBtn;

    const tools = document.createElement("div");
    tools.className = "revive-layer__tools";
    tools.append(tweakBtn, photorealBtn, galleryBtn, removeBtn);
    root.appendChild(tools);

    const status = document.createElement("p");
    status.className = "revive-layer__status";
    status.hidden = true;
    root.appendChild(status);
    this._status = status;

    const wait = document.createElement("div");
    wait.className = "revive-layer__wait";
    wait.hidden = true;
    wait.setAttribute("aria-hidden", "true");
    wait.setAttribute("aria-live", "polite");
    wait.innerHTML = `
      <div class="revive-layer__orb" aria-hidden="true">
        <span class="revive-layer__ring revive-layer__ring--outer"></span>
        <span class="revive-layer__ring revive-layer__ring--inner"></span>
        <span class="revive-layer__core"></span>
      </div>
      <p class="revive-layer__wait-kicker">Aspect Revive</p>
      <p class="revive-layer__wait-title">Letting the light in</p>
      <p class="revive-layer__wait-sub">Furniture stays. Shadows become real.</p>
    `;
    root.appendChild(wait);
    this._wait = wait;

    // --- hover targets -------------------------------------------------------
    // Items with an extracted silhouette get an exact SVG path (the polygon IS
    // the pointer target, so the highlight hugs the item's visible pixels).
    // Items without one fall back to an invisible rectangle.
    const SVG_NS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "revive-outlines");
    svg.setAttribute("viewBox", "0 0 1 1");
    svg.setAttribute("preserveAspectRatio", "none"); // matches the image's fill
    root.appendChild(svg);

    const editItems = [];
    for (const hb of state.hitboxes ?? []) {
      const polys = Array.isArray(hb.outline) && hb.outline.length
        ? hb.outline
        : hb.rect
          ? [rectToPoly(hb.rect)]
          : [];
      if (!polys.length) continue;
      const rings = polys.map(polyToRing).filter((r) => r.length >= 3);
      if (!rings.length) continue;
      const path = document.createElementNS(SVG_NS, "path");
      path.setAttribute("class", "revive-outline");
      path.setAttribute("d", rings.map(ringToD).join(""));
      path.setAttribute("vector-effect", "non-scaling-stroke");
      path.setAttribute("fill-rule", "evenodd");
      path.addEventListener("pointerenter", (e) => this._showCard(hb, path, e));
      path.addEventListener("pointerleave", () => {
        if (!this._editor?.dragging) this._hideCard();
      });
      svg.appendChild(path);
      editItems.push({ hb, path, rings });
    }

    const cursor = document.createElement("div");
    cursor.className = "revive-cursor";
    cursor.setAttribute("aria-hidden", "true");
    root.appendChild(cursor);
    this._cursor = cursor;

    this._ptrMove = (e) => {
      if (e.pointerType === "touch") return;
      const r = root.getBoundingClientRect();
      cursor.style.left = `${e.clientX - r.left}px`;
      cursor.style.top = `${e.clientY - r.top}px`;
      cursor.classList.add("revive-cursor--on");
      root.classList.add("revive-layer--cursor-on");
      if (this._hotHb && !this._editor?.dragging) this._emitHover(this._hotHb, e, true);
    };
    window.addEventListener("pointermove", this._ptrMove, { passive: true });
    root.addEventListener("pointerover", this._ptrMove, { passive: true });

    document.body.appendChild(root);
    document.body.classList.add("revive-open");
    // Double rAF so the browser paints opacity:0 before the fade-in kicks.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.add("revive-layer--in")),
    );
    this.root = root;

    this._escHandler = (e) => {
      if (e.key !== "Escape") return;
      if (document.body.classList.contains("revive-gallery-open")) return;
      if (this._editor?.hasSelection()) {
        this._editor.deselect();
        return;
      }
      this.onEnterTour();
    };
    window.addEventListener("keydown", this._escHandler);
    this._image = image ?? null;

    this._state = state;
    this._editor = new ReviveOutlineEditor({
      root,
      svg,
      items: editItems,
      onDrag: (on) => {
        if (on) this._hideCard();
      },
      onSelectionChange: (on) => {
        if (this._removeBtn) this._removeBtn.disabled = !on;
      },
      onRemove: (hb) => {
        const list = state.hitboxes;
        const i = list?.indexOf(hb) ?? -1;
        if (i >= 0) list.splice(i, 1);
        if (this._hotHb === hb) this._hideCard();
        this.onHitboxesChange(state);
      },
    });
    this._editor.bind();
    this._setTweak(true);
  }

  _setTweak(on) {
    const enabled = Boolean(on);
    this._editor?.setEnabled(enabled);
    if (this._tweakBtn) {
      this._tweakBtn.classList.toggle("is-on", enabled);
      this._tweakBtn.setAttribute("aria-pressed", String(enabled));
    }
    if (this._removeBtn) {
      this._removeBtn.hidden = !enabled;
      if (!enabled) this._removeBtn.disabled = true;
    }
    if (this.root) {
      const badge = this.root.querySelector(".revive-layer__badge");
      if (badge) {
        badge.textContent = enabled
          ? "Tweak — drag corners to scale · Del or Remove deletes the item"
          : "AI Render — hover items for live prices";
      }
    }
  }

  /** Current still shown under the hitboxes (captured frame or swapped AI image). */
  currentImage() {
    return this.root?.querySelector(".revive-layer__img")?.src ?? this._image ?? null;
  }

  /** Swap the still without rebuilding hitboxes — used by the Revive gallery. */
  async setImage(src, { photoreal = false } = {}) {
    if (!src) return;
    this._image = src;
    if (!this.root) return;
    this.root.classList.toggle("revive-layer--photoreal", photoreal);
    let img = this.root.querySelector(".revive-layer__img");
    if (!img) {
      img = document.createElement("img");
      img.className = "revive-layer__img";
      img.alt = "AI-generated render of the current view";
      img.draggable = false;
      this.root.insertBefore(img, this.root.firstChild);
      this.root.classList.remove("revive-layer--noimg");
    }
    if (img.src === src) {
      try { await img.decode(); } catch { /* already painted */ }
      return;
    }
    img.src = src;
    try { await img.decode(); } catch { /* swap anyway */ }
  }

  /** Blur the still and spin until Photo-to-Render paints. Hitboxes stay mounted. */
  setBusy(on) {
    if (!this.root) return Promise.resolve();
    const busy = Boolean(on);
    if (this._photorealBtn) this._photorealBtn.disabled = busy;
    if (this._tweakBtn) this._tweakBtn.disabled = busy;
    if (this._removeBtn) this._removeBtn.disabled = busy || !this._editor?.hasSelection();
    if (busy) this.setStatus("", "");

    if (busy) {
      this.root.classList.add("revive-layer--busy");
      this.root.classList.remove("revive-layer--reveal");
      if (this._wait) {
        this._wait.hidden = false;
        this._wait.setAttribute("aria-hidden", "false");
        requestAnimationFrame(() => this._wait?.classList.add("revive-layer__wait--on"));
      }
      return Promise.resolve();
    }

    this.root.classList.remove("revive-layer--busy");
    this.root.classList.add("revive-layer--reveal");
    this._wait?.classList.remove("revive-layer__wait--on");
    this._wait?.setAttribute("aria-hidden", "true");
    return new Promise((resolve) => {
      window.setTimeout(() => {
        if (this._wait) this._wait.hidden = true;
        this.root?.classList.remove("revive-layer--reveal");
        resolve();
      }, 920);
    });
  }

  setStatus(message, kind = "") {
    if (!this._status) return;
    this._status.hidden = !message;
    this._status.textContent = message || "";
    this._status.dataset.kind = kind;
  }

  _hoverInfo(hb) {
    return {
      id: hb.id,
      name: hb.name,
      brand: hb.brand || null,
      category: hb.category ?? null,
      price: hb.price ?? hb.pricing?.list ?? null,
      dims: hb.dims ?? null,
    };
  }

  _emitHover(hb, e, follow = false) {
    const screen = e
      ? { x: e.clientX, y: e.clientY }
      : null;
    this.onHover({ info: this._hoverInfo(hb), screen, follow });
  }

  _showCard(hb, hitEl, e) {
    this._hotHb = hb;
    if (e) {
      this._emitHover(hb, e, false);
    } else {
      const r = hitEl.getBoundingClientRect();
      this.onHover({
        info: this._hoverInfo(hb),
        screen: { x: r.left + r.width / 2, y: r.top + r.height * 0.28 },
      });
    }
    document.body.classList.add("revive-hover");
    this._cursor?.classList.add("revive-cursor--hot");
    hitEl.classList.add(
      hitEl instanceof SVGElement ? "revive-outline--on" : "revive-hit--on",
    );
  }

  _hideCard() {
    this._hotHb = null;
    this.onHover({ info: null });
    document.body.classList.remove("revive-hover");
    this._cursor?.classList.remove("revive-cursor--hot");
    this.root
      ?.querySelectorAll(".revive-hit--on, .revive-outline--on")
      .forEach((el) =>
        el.classList.remove("revive-hit--on", "revive-outline--on"),
      );
  }

  /**
   * Cross-fade the 2D layer away over the (already re-rendered) live canvas,
   * then remove it. Resolves when the fade completes.
   */
  fadeOutAndRemove(ms = 500) {
    const root = this.root;
    if (!root) return Promise.resolve();
    try { this._hideCard(); } catch { /* overlay may already be mid-teardown */ }
    try { this._editor?.destroy(); } catch { /* chrome already gone */ }
    this._editor = null;
    this.root = null;
    this._unbindPointer();
    window.removeEventListener("keydown", this._escHandler);
    this._escHandler = null;
    // Photoreal stills are huge data-URLs — drop the bitmap so WebGL can present.
    const img = root.querySelector(".revive-layer__img");
    if (img) {
      img.removeAttribute("src");
      img.remove();
    }
    root.style.transitionDuration = `${ms}ms`;
    root.classList.add("revive-layer--out");
    return new Promise((resolve) => {
      setTimeout(() => {
        root.remove();
        document.body.classList.remove("revive-open", "revive-hover");
        resolve();
      }, ms + 40);
    });
  }

  _unbindPointer() {
    if (this._ptrMove) window.removeEventListener("pointermove", this._ptrMove);
    this._ptrMove = null;
    this._cursor = null;
  }

  /** Instant teardown (room reload etc.). */
  remove() {
    this._hideCard();
    this._editor?.destroy();
    this._editor = null;
    if (this._escHandler) window.removeEventListener("keydown", this._escHandler);
    this._escHandler = null;
    this._unbindPointer();
    this.root?.remove();
    this.root = null;
    this._photorealBtn = null;
    this._tweakBtn = null;
    this._removeBtn = null;
    this._state = null;
    this._status = null;
    this._wait = null;
    document.body.classList.remove("revive-open", "revive-hover");
  }
}

export default ReviveOverlay;
